/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { get } from 'lodash';
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { AlertBar, Modal, Text } from '@blend/enterprise-components';
import { ModalContent } from 'blb-components/ModalContent';
import { Field, Form } from 'react-final-form';
import { ActionButton } from '@blend/enterprise-components';
import { CurrentRequestType, FeeFormValue } from '../../types';
import { getRequestFields } from './inputConfigs';
import { formatUserForMessage } from '../../../Inbox/utils/threadUtils';
import { useContext } from 'react';
import { FeesContext } from '../../containers/FeesTab';
import { BlendBridge } from 'blb-types/blendBridge';
import { isNil, omit, cloneDeep, map } from 'lodash';
import { REQUEST_MODAL_COPY, TYPE_TO_STATUS } from '../../constants';
import { Pricing } from 'blb-types/pricing';
import { ReduxProps } from './NewRequestModalContainer';

const styles = StyleSheet.create({
  disclosureWrapper: {
    marginTop: 4,
  },
  errorWrapper: {
    marginBottom: 10,
  },
  fieldWrapper: {
    alignItems: 'center',
    flexBasis: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  footerBar: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 28,
  },
  row: {
    ':not(:last-child)': {
      marginBottom: 14,
    },
  },
  subheaderWrapper: {
    marginBottom: 24,
    marginTop: -10,
  },
});

export interface NewRequestValues {
  amount: number;
  message?: string;
  name: string;
  paidBy: Pricing.PayingParty;
  paidTo: Pricing.PayeeParty;
  paymentMilestone: Pricing.PaymentMilestone;
  dataType: Pricing.DataType;
  percentageAmount?: number;
  baseValueForPercentage?: number;
  numberOfTimePeriods?: number;
  amountPerTimePeriod?: number;
}

export interface NewRequestModalProps {
  currentRequest: CurrentRequestType;
  handleClose: () => void;
  isOpen: boolean;
}

export type Props = ReduxProps & NewRequestModalProps;
export const NewRequestModal: React.FC<Props> = ({ currentRequest, isOpen, handleClose, user, fetchUserError }) => {
  const { order, setFormValues, formValues, loan, setErrorType } = useContext(FeesContext);

  const getTotalAmount = (values: NewRequestValues) => {
    const { percentageAmount, numberOfTimePeriods, amountPerTimePeriod, baseValueForPercentage, dataType } = values;
    if (dataType === 'Percentage' && percentageAmount && baseValueForPercentage) {
      return baseValueForPercentage * (percentageAmount * 0.01);
    } else if (['TimeMonths', 'TimeDays'].includes(dataType) && numberOfTimePeriods && amountPerTimePeriod) {
      return numberOfTimePeriods * amountPerTimePeriod;
    }

    return 0;
  };

  const getAdditionalFieldsBasedOnDataType = (values: NewRequestValues): Partial<FeeFormValue> => {
    if (values.dataType === 'Percentage') {
      return {
        percentageAmount: values.percentageAmount,
        baseValueForPercentage: values.baseValueForPercentage,
        amount: getTotalAmount(values),
      };
    } else if (values.dataType === 'TimeDays' || values.dataType === 'TimeMonths') {
      return {
        numberOfTimePeriods: values.numberOfTimePeriods,
        amountPerTimePeriod: values.amountPerTimePeriod,
        amount: getTotalAmount(values),
      };
    }

    return {};
  };

  const generateFee = (values: NewRequestValues): FeeFormValue => {
    return {
      ...omit(values, 'message'),
      dataType: get(values, 'dataType', 'Fixed'),
      isSavedInLOS: false,
      loanEstimateSection: currentRequest.subsectionKey,
      loanId: loan!.loanId,
      managedBy: get(values, 'managedBy', Pricing.FeeSystem.BLEND_TITLE_WORKSPACE),
      providerName: get(values, 'providerName', 'BlendTemplate'),
      status: Pricing.FeeStatus.IN_PROGRESS,
      updateType: TYPE_TO_STATUS[currentRequest.type],
      ...getAdditionalFieldsBasedOnDataType(values),
    };
  };

  const generateThread = (message: string, name: string) => {
    return {
      attachments: [],
      body: message,
      subject: `Fee ${currentRequest.type} request: ${name}`,
      tenant: order!.tenant,
      topic: BlendBridge.API.Topic.FEE_COLLABORATION,
      user: formatUserForMessage(user),
    };
  };

  const onSubmit = (values: NewRequestValues) => {
    const formSubsection = cloneDeep(formValues[currentRequest.subsectionKey]);
    if (isNil(formSubsection)) {
      setErrorType('CreateRequestError');
    } else {
      const { message, name } = values;
      let fee = generateFee(values);
      if (!isNil(message)) {
        const thread = generateThread(message, name);
        fee = { ...fee, thread };
      }

      switch (currentRequest.type) {
        case 'add':
          formSubsection.push(fee);
          break;
        case 'edit':
        case 'delete':
          formSubsection[currentRequest.rowIdx] = fee;
          break;
      }

      setFormValues({
        ...formValues,
        ...{ [currentRequest.subsectionKey]: formSubsection },
      });
    }

    handleClose();
  };

  const getInitialValues = () => {
    const { subsectionKey, rowIdx, type } = currentRequest;
    const subsection = formValues[subsectionKey];
    if (type !== 'add' && !isNil(subsection)) {
      return subsection[rowIdx];
    }

    return {};
  };

  const renderMessageDisclosure = () => (
    <div className={css(styles.disclosureWrapper)}>
      <Text colorName="NIGHT_80" fontSize="sm">
        This message will appear in your shared inbox for further collaboration once you submit your changes.
      </Text>
    </div>
  );

  const renderFields = (values: NewRequestValues) => {
    const requestFields = getRequestFields(currentRequest.subsectionKey, values.dataType);

    return map(requestFields[currentRequest.type], fieldConfig => {
      return (
        <div className={css(styles.row)} key={`NewRequest_${fieldConfig.name}`}>
          <div className={css(styles.fieldWrapper)}>
            {fieldConfig.name === 'totalAmount' ? (
              <Field {...fieldConfig} placeholder={getTotalAmount(values)} />
            ) : (
              <Field {...fieldConfig} />
            )}
          </div>
          {fieldConfig.name === 'message' && renderMessageDisclosure()}
        </div>
      );
    });
  };

  return (
    <Modal contentLabel="New Request" handleClose={handleClose} isOpen={isOpen}>
      <ModalContent
        handleClose={handleClose}
        headerText={REQUEST_MODAL_COPY[currentRequest.type].header}
        id="NewRequestModalContent"
      >
        <div className={css(styles.subheaderWrapper)}>
          <Text colorName="NIGHT_80" fontSize="md">
            {REQUEST_MODAL_COPY[currentRequest.type].subheader}
          </Text>
        </div>

        {fetchUserError && (
          <div className={css(styles.errorWrapper)}>
            <AlertBar
              id="it-NewRequestModal-ErrorAlert"
              message="Error fetching user. Please try again later."
              theme="error"
            />
          </div>
        )}

        <Form initialValues={getInitialValues()} onSubmit={onSubmit}>
          {({ handleSubmit, values }) => {
            return (
              <form id="it-NewRequestForm" onSubmit={handleSubmit}>
                {renderFields(values)}
                <div className={css(styles.footerBar)}>
                  <ActionButton id="NewRequestForm-cancel" label="Cancel" onClick={handleClose} theme="tertiary" />
                  <ActionButton
                    disabled={!user || !!fetchUserError}
                    id="NewRequestForm-save"
                    label="Save"
                    type="submit"
                  />
                </div>
              </form>
            );
          }}
        </Form>
      </ModalContent>
    </Modal>
  );
};
